"use client";

import Link from "next/link";
import { useRef } from "react";
import { Tag } from "primereact/tag";
import { Column } from "primereact/column";
import Image from "next/image";

import EnhancedTable from "@/hocs/EnhancedTable";
import { endDate, startDate } from "@/lib/dayjs";
import customizationApi from "@/api/customization";
import { useToastStore } from "@/stores/toast.store";
import { NO_IMAGE_SRC_PATH } from "@/constants/image";
import { copyToClipboard, formatDateWithTimezone } from "@/utils/func";
import useTimezoneStore from "@/stores/timezone.store";
import { printStatusConfig } from "@/constants/status";
import { filterOrderOptions } from "@/constants/filter";
import CustomButton from "@/components/common/CustomButton";
import { Customization } from "@/interfaces/customization.interface";
import { MoveUpRight } from "lucide-react";

const ROWS_PER_PAGE = 10;

const CustomizationsPage = () => {
  const refetchRef = useRef<(() => void) | null>(null);

  const handlePrintImage = async (customizationId: string) => {
    try {
      await customizationApi.printManually(customizationId);
      useToastStore
        .getState()
        .showToast(
          "info",
          "Print job queued",
          "Your print request has been added to the processing queue",
        );
      refetchRef.current?.();
    } catch (error) {
      console.log(error);
      useToastStore
        .getState()
        .showToast(
          "error",
          "Failed to queue print job",
          "Please try again later",
        );
    }
  };

  const displayTimeZone = useTimezoneStore((s) => s.timezone);

  return (
    <div className="flex h-full w-full flex-col rounded-lg bg-white p-6 shadow-lg">
      <EnhancedTable
        size="small"
        cachedKey="customizations"
        fetchData={(params) =>
          customizationApi.getMany({ ...params, view: "list" })
        }
        itemsPerPage={ROWS_PER_PAGE}
        searchPlaceholder="Search customizations by id, campaign name, order id"
        title="Carts"
        scrollable
        filterOptions={filterOrderOptions}
        dateConfig={{
          fields: ["startDate", "endDate"],
          defaultValues: [startDate.toDate(), endDate.toDate()],
        }}
        refetchRef={refetchRef}
      >
        <Column
          header="Preview"
          style={{ width: "150px" }}
          bodyClassName="text-center"
          body={(rowData: Customization) => (
            <div className="group relative flex h-16 w-16 cursor-pointer rounded border border-gray-300">
              <Image
                crossOrigin="anonymous"
                width={200}
                height={200}
                src={rowData?.previewImageUrl || NO_IMAGE_SRC_PATH}
                alt="Preview Image"
                className="transition-opacity hover:opacity-80"
                loading="lazy"
              />

              <div
                className="absolute hidden h-full w-full items-center justify-center gap-2 bg-[#696b7073] group-hover:flex"
                onMouseUp={(e) => {
                  if (e.button === 1) {
                    if (rowData?.previewImageUrl) {
                      e.preventDefault();
                      window.open(rowData?.previewImageUrl, "_blank");
                    }
                  }
                }}
                onClick={() => {
                  if (rowData?.previewImageUrl) {
                    window.open(rowData.previewImageUrl, "_blank");
                  }
                }}
              >
                <MoveUpRight size={20} />
              </div>
            </div>
          )}
        />
        <Column
          header="Print Image"
          style={{ width: "150px" }}
          bodyClassName="text-center"
          body={(rowData: Customization) => (
            <div className="group relative flex h-16 w-16 cursor-pointer rounded border border-gray-300">
              <Image
                crossOrigin="anonymous"
                width={200}
                height={200}
                src={rowData?.printImageUrl || NO_IMAGE_SRC_PATH}
                alt="Print Image"
                className="transition-opacity hover:opacity-80"
                loading="lazy"
              />

              <div
                className="absolute hidden h-full w-full items-center justify-center gap-2 bg-[#696b7073] group-hover:flex"
                onMouseUp={(e) => {
                  if (e.button === 1) {
                    if (rowData?.printImageUrl) {
                      e.preventDefault();
                      window.open(rowData?.printImageUrl, "_blank");
                    }
                  }
                }}
                onClick={() => {
                  if (rowData?.printImageUrl) {
                    window.open(rowData.printImageUrl, "_blank");
                  }
                }}
              >
                <MoveUpRight size={20} />
              </div>
            </div>
          )}
        />
        <Column
          field="_id"
          header="Cart ID"
          body={(rowData: Customization) => (
            <div
              className="relative inline-block cursor-pointer text-gray-800"
              title="Click to copy ID"
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(rowData._id);
              }}
            >
              {rowData._id}

              {rowData.liveMode === false && (
                <Tag
                  value="Test"
                  severity="warning"
                  className="absolute -top-2 -right-8 !px-1 !py-0 !text-[10px] leading-none"
                />
              )}
            </div>
          )}
        />

        <Column
          field="campaignId"
          header="Campaign"
          style={{ minWidth: "500px", maxWidth: "500px" }}
          bodyClassName="text-start"
          body={(rowData: Customization) => (
            <div className="flex flex-col gap-1">
              <Link
                href={`/campaigns/${rowData.campaign?._id}`}
                target="_blank"
                className="line-clamp-2 w-full transition-all hover:underline"
              >
                <span className="text-gray-800">
                  {rowData.campaign?.title || "N/A"}
                </span>
              </Link>
            </div>
          )}
        />
        <Column
          header="Print Status"
          style={{ width: "140px" }}
          bodyClassName="text-center"
          body={(rowData: Customization) => {
            const statusKey = rowData.printStatus || "default";
            const config =
              printStatusConfig[statusKey] || printStatusConfig.default;
            return (
              <Tag
                value={config.label}
                severity={config.severity}
                icon={config.icon}
                style={{ backgroundColor: config.color, color: "white" }}
              />
            );
          }}
        />
        <Column
          header="Timestamp"
          field="createdAt"
          body={(rowData: Customization) => {
            return (
              <p>
                <span
                  className="text-gray-500"
                  style={{
                    fontSize: "0.8rem",
                  }}
                >
                  {formatDateWithTimezone(rowData.createdAt, displayTimeZone)}
                </span>
                <span className="flex h-max w-full items-center justify-center">
                  <i
                    className="pi pi-arrow-down text-sm text-gray-500"
                    style={{
                      fontSize: "0.5rem",
                    }}
                  ></i>
                </span>
                <span
                  className="text-gray-500"
                  style={{
                    fontSize: "0.8rem",
                  }}
                >
                  {formatDateWithTimezone(rowData.updatedAt, displayTimeZone)}
                </span>
              </p>
            );
          }}
          style={{ width: "70px", whiteSpace: "nowrap", textAlign: "start" }}
        />

        <Column
          header="Actions"
          frozen
          alignFrozen="right"
          style={{ width: "80px" }}
          bodyClassName="text-center"
          body={(customization: Customization) => (
            <div className="flex items-center justify-center gap-2">
              <CustomButton
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full hover:bg-gray-200"
                onClick={() => handlePrintImage(customization._id)}
                tooltip="Print image"
                tooltipOptions={{ position: "top" }}
              >
                <i className="pi pi-print text-lg text-blue-600" />
              </CustomButton>

              {customization.campaign?.productLink &&
                customization.campaign?.productSlug && (
                  <Link
                    href={`${customization.campaign.productLink}?customizationId=${customization._id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <CustomButton
                      className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full hover:bg-gray-200"
                      tooltip="Refill"
                      tooltipOptions={{ position: "top" }}
                    >
                      <i className="pi pi-refresh text-lg text-green-600" />
                    </CustomButton>
                  </Link>
                )}
            </div>
          )}
        />
      </EnhancedTable>
    </div>
  );
};

export default CustomizationsPage;
